import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useT, useI18n } from "@/i18n/I18nContext";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { getAttribution } from "@/hooks/useUtmCapture";
import VoiceRecorder from "@/components/VoiceRecorder";
import StatuteCountdown from "@/components/StatuteCountdown";
import SeoHead from "@/components/SeoHead";
import ReferralSourceSelect from "@/components/ReferralSourceSelect";

const ACCIDENT_TYPES = [
  ["Rear-end collision", "intake.type.rear_end"],
  ["Head-on collision", "intake.type.head_on"],
  ["T-bone / side-impact", "intake.type.tbone"],
  ["Multi-vehicle pileup", "intake.type.pileup"],
  ["Hit and run", "intake.type.hit_run"],
  ["Pedestrian struck", "intake.type.pedestrian"],
  ["Motorcycle accident", "intake.type.motorcycle"],
  ["Rideshare (Uber/Lyft)", "intake.type.rideshare"],
  ["Truck / 18-wheeler", "intake.type.truck"],
  ["Other", "intake.type.other"],
];

const FAULT_OPTIONS = [
  ["Other driver", "intake.fault.other"],
  ["Me", "intake.fault.me"],
  ["Shared fault", "intake.fault.shared"],
  ["Not sure", "intake.fault.unsure"],
];

export default function LeadIntake() {
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useI18n();
  const STEP_KEYS = [t("intake.step.accident"), t("intake.step.injuries"), t("intake.step.contact")];
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [referral, setReferral] = useState({ type: "", referrerId: "", hint: "" });
  const [form, setForm] = useState({
    state: "NY",
    city: "",
    accident_date: "",
    accident_type: "",
    at_fault: "",
    police_report: false,
    injuries: "",
    medical_treatment: false,
    has_attorney: false,
    description: "",
    full_name: "",
    email: "",
    phone: "",
    source: "intake-form",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return form.state && form.accident_type && form.accident_date;
    if (step === 1) return form.injuries.trim().length > 0;
    if (step === 2) return form.full_name && form.email && form.phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const attribution = getAttribution();
      const referralPayload = referral.type
        ? {
            referral_source_type: referral.type,
            referrer_id: referral.referrerId && referral.referrerId !== "__other__" ? referral.referrerId : undefined,
            referrer_name_hint: referral.hint || undefined,
          }
        : {};
      await api.post("/leads", { ...form, ...attribution, ...referralPayload, language: lang });
      setDone(true);
      toast.success(t("intake.toast.success"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <section className="mx-auto max-w-3xl px-6 py-24 lg:py-32" data-testid="intake-success">
          <div className="card-soft p-10 text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full" style={{ background: "#E6F6ED" }}>
              <CheckCircle2 className="h-7 w-7 text-[#2D6A4F]" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
              {t("intake.success.title")}
            </h1>
            <p className="mt-4 text-[#5C6B68]">
              {t("intake.success.body", { state: form.state })}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href="tel:+18005551234" className="btn-primary" data-testid="success-call">{t("intake.success.call")} 1-800-555-1234</a>
              <button className="btn-secondary" onClick={() => navigate("/")} data-testid="success-home">{t("intake.success.home")}</button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SeoHead
        title="Free MVA Case Review — Apex MVA · NY & NJ"
        description="2-minute confidential intake form. Tell us what happened and we'll match you with a vetted NY/NJ personal-injury attorney. No fee unless we win."
      />
      <Navbar />
      <section className="mx-auto max-w-3xl px-6 py-16 lg:py-20">
        <span className="label-cap">{t("intake.label")}</span>
        <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
          {t("intake.title")}
        </h1>
        <p className="mt-3 text-[#5C6B68]">{t("intake.subtitle")}</p>

        <div className="mt-8 card-soft p-6 sm:p-10" data-testid="intake-form">
          <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-[0.15em] text-[#5C6B68]">
            <span>{t("intake.step", { n: step + 1, total: STEP_KEYS.length, label: STEP_KEYS[step] })}</span>
            <span className="inline-flex items-center gap-1.5 text-[#11322A]"><ShieldCheck className="h-3.5 w-3.5" /> {t("intake.encrypted")}</span>
          </div>
          <Progress value={((step + 1) / STEP_KEYS.length) * 100} className="h-1.5" data-testid="intake-progress" />

          <div className="mt-8 space-y-5">
            {step === 0 && (
              <>
                <div>
                  <label className="label-cap">{t("intake.state_label")}</label>
                  <div className="mt-2 flex gap-3">
                    {["NY", "NJ"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => set("state", s)}
                        className={`flex-1 rounded-lg border-2 py-3 font-display text-lg font-bold transition ${form.state === s ? "border-[#11322A] bg-[#11322A] text-white" : "border-[#E0E4E2] text-[#0B1317] hover:border-[#11322A]"}`}
                        data-testid={`intake-state-${s}`}
                      >
                        {s === "NY" ? t("intake.state_ny") : t("intake.state_nj")}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="label-cap">{t("intake.date_label")}</label>
                    <input type="date" className="input-field mt-2" value={form.accident_date} onChange={(e) => set("accident_date", e.target.value)} data-testid="intake-date" />
                  </div>
                  <div>
                    <label className="label-cap">{t("intake.city_label")}</label>
                    <input className="input-field mt-2" placeholder={t("intake.city_placeholder")} value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="intake-city" />
                  </div>
                </div>
                <StatuteCountdown accidentDate={form.accident_date} state={form.state} />
                <div>
                  <label className="label-cap">{t("intake.type_label")}</label>
                  <select className="input-field mt-2" value={form.accident_type} onChange={(e) => set("accident_type", e.target.value)} data-testid="intake-accident-type">
                    <option value="">{t("intake.select")}</option>
                    {ACCIDENT_TYPES.map(([value, key]) => (
                      <option key={value} value={value}>{t(key)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-cap">{t("intake.fault_label")}</label>
                  <select className="input-field mt-2" value={form.at_fault} onChange={(e) => set("at_fault", e.target.value)} data-testid="intake-fault">
                    <option value="">{t("intake.select")}</option>
                    {FAULT_OPTIONS.map(([value, key]) => (
                      <option key={value} value={value}>{t(key)}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-3 text-sm font-semibold text-[#0B1317]">
                  <input type="checkbox" checked={form.police_report} onChange={(e) => set("police_report", e.target.checked)} data-testid="intake-police" />
                  {t("intake.police_label")}
                </label>
              </>
            )}

            {step === 1 && (
              <>
                <div>
                  <label className="label-cap">{t("intake.injuries_label")}</label>
                  <textarea rows={4} className="input-field mt-2" placeholder={t("intake.injuries_placeholder")} value={form.injuries} onChange={(e) => set("injuries", e.target.value)} data-testid="intake-injuries" />
                  <VoiceRecorder onTranscribed={(text) => set("injuries", form.injuries ? `${form.injuries} ${text}` : text)} />
                </div>
                <label className="flex items-center gap-3 text-sm font-semibold text-[#0B1317]">
                  <input type="checkbox" checked={form.medical_treatment} onChange={(e) => set("medical_treatment", e.target.checked)} data-testid="intake-treatment" />
                  {t("intake.treatment_label")}
                </label>
                <label className="flex items-center gap-3 text-sm font-semibold text-[#0B1317]">
                  <input type="checkbox" checked={form.has_attorney} onChange={(e) => set("has_attorney", e.target.checked)} data-testid="intake-attorney" />
                  {t("intake.has_attorney_label")}
                </label>
                <div>
                  <label className="label-cap">{t("intake.notes_label")}</label>
                  <textarea rows={4} className="input-field mt-2" placeholder={t("intake.notes_placeholder")} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="intake-description" />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <label className="label-cap">{t("intake.name_label")}</label>
                  <input className="input-field mt-2" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="intake-name" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="label-cap">{t("intake.email_label")}</label>
                    <input type="email" className="input-field mt-2" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="intake-email" />
                  </div>
                  <div>
                    <label className="label-cap">{t("intake.phone_label")}</label>
                    <input type="tel" className="input-field mt-2" placeholder={t("intake.phone_placeholder")} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="intake-phone" />
                  </div>
                </div>
                <ReferralSourceSelect value={referral} onChange={setReferral} />
                <p className="text-xs text-[#5C6B68]">
                  {t("intake.consent")}
                </p>
              </>
            )}
          </div>

          <div className="mt-10 flex items-center justify-between">
            <button
              type="button"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#5C6B68] hover:text-[#11322A] disabled:opacity-40"
              disabled={step === 0}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              data-testid="intake-back"
            >
              <ArrowLeft className="h-4 w-4" /> {t("back")}
            </button>
            {step < STEP_KEYS.length - 1 ? (
              <button
                type="button"
                className="btn-primary disabled:opacity-50"
                disabled={!canNext()}
                onClick={() => setStep((s) => s + 1)}
                data-testid="intake-next"
              >
                {t("continue")} <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary disabled:opacity-50"
                disabled={!canNext() || submitting}
                onClick={submit}
                data-testid="intake-submit"
              >
                {submitting ? t("intake.submitting") : t("intake.submit")}
              </button>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
